import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuListFilter, LuX } from 'react-icons/lu';
import Popover from '../../../components/ui/Popover';
import { GLASS_FIELD } from '../../../components/ui/glass';
import { useTranslation } from '../../../i18n/LanguageContext';
import { columnLabel, getFilterState, optionLabel } from '../employeeColumns';

const BUTTON_STATES = {
  off: 'tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:tw-text-slate-500 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-slate-200',
  applied: 'tw-bg-teal-500/15 tw-text-teal-700 hover:tw-bg-teal-500/25 dark:tw-bg-teal-400/15 dark:tw-text-teal-300',
  pending: 'tw-bg-amber-400/20 tw-text-amber-700 hover:tw-bg-amber-400/30 dark:tw-bg-amber-400/15 dark:tw-text-amber-300',
};

const optionClass = (selected) => [
  'tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-transition-colors',
  'hover:tw-bg-slate-900/[0.05] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:hover:tw-bg-white/[0.07]',
  selected ? 'tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300' : 'tw-font-medium tw-text-slate-700 dark:tw-text-slate-200',
].join(' ');

/** Filter trigger in a column header, with the column's filter in a popover. */
const ColumnFilterButton = ({ columnKey, column, value, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const label = columnLabel(t, columnKey);
  const title = t('employees.filter.title', { label });
  const state = getFilterState(columnKey, value);
  const current = value === undefined || value === null ? '' : String(value);
  const close = () => setOpen(false);
  const setValue = (next) => onChange(columnKey, next);

  const renderBody = () => {
    if (column.type === 'select') {
      const choices = [
        { value: '', label: t('employees.filter.all') },
        ...column.options.map((option) => ({ value: String(option.value), label: optionLabel(t, option) })),
      ];
      return (
        <div className="tw-space-y-0.5">
          {choices.map((choice) => {
            const selected = choice.value === current;
            return (
              <button
                key={choice.value || 'all'}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  setValue(choice.value);
                  close();
                }}
                className={optionClass(selected)}
              >
                <span className="tw-truncate">{choice.label}</span>
                {selected && <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />}
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div className="tw-px-1 tw-pb-1">
        <input
          data-autofocus
          type={column.type === 'date' ? 'date' : 'text'}
          value={current}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') close();
          }}
          placeholder={column.type === 'date' ? undefined : t('employees.filter.placeholder')}
          aria-label={title}
          className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
        />
        {state === 'pending' && (
          <p className="tw-mt-2 tw-px-1 tw-text-xs tw-font-medium tw-text-amber-700 dark:tw-text-amber-300">{t('employees.filter.minChars')}</p>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={state === 'off' ? title : t('employees.filter.titleOn', { label })}
        title={title}
        className={`tw-relative tw-grid tw-h-6 tw-w-6 tw-shrink-0 tw-place-items-center tw-rounded-full tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${BUTTON_STATES[state]} ${
          open ? 'tw-ring-2 tw-ring-teal-500/40' : ''
        }`}
      >
        <LuListFilter aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
      </button>

      <Popover
        anchorRef={buttonRef}
        open={open}
        onClose={close}
        role={column.type === 'select' ? 'menu' : 'dialog'}
        label={title}
        className="tw-w-64 tw-p-2"
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-pb-2 tw-pt-1">
          <p className="tw-truncate tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-500 dark:tw-text-slate-400">
            {label}
          </p>
          {current !== '' && column.type !== 'select' && (
            <button
              type="button"
              onClick={() => setValue('')}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-transparent tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-900/[0.05] hover:tw-text-slate-800 dark:tw-text-slate-400 dark:hover:tw-bg-white/[0.07] dark:hover:tw-text-white"
            >
              <LuX aria-hidden="true" className="tw-h-3 tw-w-3" />
              {t('employees.filter.clear')}
            </button>
          )}
        </div>
        {renderBody()}
      </Popover>
    </>
  );
};

ColumnFilterButton.propTypes = {
  columnKey: PropTypes.string.isRequired,
  column: PropTypes.shape({
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['text', 'select', 'date']).isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), label: PropTypes.string })),
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
};

export default ColumnFilterButton;
